/**
 * Explore calculations for the Particle Model & States Lab (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"): density, compression in a sealed syringe and diffusion
 * against temperature. Each function takes the learner's typed values, throws the first field
 * error on invalid setup, and returns the worked steps built from the same numbers that drive the
 * visuals. Constants are the reviewed values in `particleModel.ts`; nothing new is introduced here.
 */
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
} from './modelInputContract';
import {
  COMPRESSIBILITY_PER_PA,
  DENSITY_SUBSTANCES,
  DIFFUSION_MAX_TEMPERATURE_C,
  DIFFUSION_MIN_TEMPERATURE_C,
  DIFFUSION_REFERENCE_C,
  GAS_DIFFUSION_TEMPERATURE_EXPONENT,
  IDENTIFY_TOLERANCE,
  KELVIN_OFFSET,
  PASCALS_PER_KPA,
  START_PRESSURE_KPA,
  TYPICAL_DIFFUSION_COEFFICIENT,
  compressedVolumeMl,
  densityOf,
  diffusionCoefficient,
  identifyByDensity,
  waterViscosity,
  type DensitySubstance,
  type DiffusionSetup,
  type MatterState,
} from './particleModel';

export type ExploreTab = 'density' | 'compression' | 'diffusion';

export const EXPLORE_TABS: Array<{ id: ExploreTab; label: string }> = [
  { id: 'density', label: 'Density' },
  { id: 'compression', label: 'Compression' },
  { id: 'diffusion', label: 'Diffusion' },
];

export interface ExploreResult {
  steps: WorkedStep[];
  /** One-line answer shown under the working. */
  result: string;
  /** Set when the input reaches or passes the model's reviewed range. */
  limitNote: string | null;
}

const SUPERSCRIPT: Record<string, string> = {
  '-': '⁻',
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

/** Scientific notation with three significant figures, for example 3.40 × 10⁻⁹. */
export function formatScientific(value: number): string {
  const [mantissa, exponent] = value.toExponential(2).split('e');
  const power = String(Number(exponent))
    .split('')
    .map((character) => SUPERSCRIPT[character] ?? character)
    .join('');
  return `${mantissa} × 10${power}`;
}

/** Three significant figures for densities, with thousands separators above 1000. */
export function formatDensity(value: number): string {
  if (value >= 1000) return formatModelNumber(value, 0);
  return value.toPrecision(3);
}

function formatPercent(fraction: number): string {
  return `${formatModelNumber(fraction * 100, 1)}%`;
}

// Density ---------------------------------------------------------------------------------------

export interface DensityExploreSetup {
  massG: number;
  volumeCm3: number;
}

export const DENSITY_FIELDS: ModelField<keyof DensityExploreSetup>[] = [
  { key: 'massG', label: 'Sample mass', unit: 'g', min: 0.1, max: 1000, step: 0.1 },
  { key: 'volumeCm3', label: 'Sample volume', unit: 'cm³', min: 0.1, max: 1000, step: 0.1 },
];

export const DEFAULT_DENSITY_SETUP: DensityExploreSetup = { massG: 81, volumeCm3: 30 };

export const DENSITY_PRESETS: Array<{ id: string; label: string; setup: DensityExploreSetup }> = [
  { id: 'aluminium', label: 'Aluminium block', setup: { massG: 81, volumeCm3: 30 } },
  { id: 'ethanol', label: '100 cm³ of ethanol', setup: { massG: 78.9, volumeCm3: 100 } },
  { id: 'ice', label: 'Ice cube', setup: { massG: 22.9, volumeCm3: 25 } },
  { id: 'gold', label: 'Gold ring', setup: { massG: 9.66, volumeCm3: 0.5 } },
];

export type FloatResult = 'floats' | 'sinks' | 'same';

export interface DensityExplore extends ExploreResult {
  densityGPerCm3: number;
  /** The reviewed substance within the tolerance, or null when there is no single match. */
  match: DensitySubstance | null;
  /** The reviewed substance with the smallest relative difference, shown for context. */
  nearest: DensitySubstance;
  nearestDifference: number;
  floatResult: FloatResult;
}

function relativeDifference(density: number, item: DensitySubstance): number {
  return Math.abs(density - item.densityGPerCm3) / item.densityGPerCm3;
}

const DENSITIES = DENSITY_SUBSTANCES.map((item) => item.densityGPerCm3);
const LEAST_DENSE = Math.min(...DENSITIES);
const MOST_DENSE = Math.max(...DENSITIES);

export function exploreDensity(setup: DensityExploreSetup): DensityExplore {
  const error = firstModelError(DENSITY_FIELDS, setup);
  if (error) throw new Error(error);
  const water = densityOf('water');
  if (!water) throw new Error('Reviewed water density is missing.');

  const density = setup.massG / setup.volumeCm3;
  const match = identifyByDensity(density);
  const nearest = DENSITY_SUBSTANCES.reduce((best, item) =>
    relativeDifference(density, item) < relativeDifference(density, best) ? item : best,
  );
  const nearestDifference = relativeDifference(density, nearest);
  const waterText = `${formatDensity(water.densityGPerCm3)} g/cm³`;
  const sameAsWater = formatDensity(density) === formatDensity(water.densityGPerCm3);
  const floatResult: FloatResult = sameAsWater ? 'same' : density < water.densityGPerCm3 ? 'floats' : 'sinks';
  const densityText = `${formatDensity(density)} g/cm³`;

  const tolerance = formatPercent(IDENTIFY_TOLERANCE);
  const steps: WorkedStep[] = [
    {
      id: 'density',
      title: 'Density',
      formula: 'ρ = m ÷ V',
      working: `${formatModelNumber(setup.massG, 2)} g ÷ ${formatModelNumber(setup.volumeCm3, 2)} cm³ = ${densityText}`,
      status: 'done',
    },
    {
      id: 'match',
      title: 'Compare with reviewed substances',
      formula: `|ρ − ρ(substance)| ÷ ρ(substance) ≤ ${tolerance}`,
      working: match
        ? `|${formatDensity(density)} − ${formatDensity(match.densityGPerCm3)}| ÷ ${formatDensity(match.densityGPerCm3)} = ${formatPercent(relativeDifference(density, match))} → ${match.name}`
        : `closest is ${nearest.name} (${formatDensity(nearest.densityGPerCm3)} g/cm³), ${formatPercent(nearestDifference)} away → no reviewed match`,
      status: 'done',
    },
    {
      id: 'float',
      title: 'Float or sink in water at 20 °C',
      formula: `ρ compared with water, ${waterText}`,
      working:
        floatResult === 'same'
          ? `${densityText} = ${waterText} → neither floats nor sinks`
          : floatResult === 'floats'
            ? `${densityText} < ${waterText} → floats`
            : `${densityText} > ${waterText} → sinks`,
      status: 'done',
    },
  ];

  const matchText = match ? `matches ${match.name}` : `no reviewed match within ${tolerance}`;
  const floatText = floatResult === 'same' ? 'same density as water' : `${floatResult} in water`;
  let limitNote: string | null = null;
  if (density > MOST_DENSE * (1 + IDENTIFY_TOLERANCE)) {
    limitNote = `${densityText} is denser than every substance in the reviewed table (densest: ${formatDensity(MOST_DENSE)} g/cm³). The model does not guess a substance; check the mass and volume.`;
  } else if (density < LEAST_DENSE * (1 - IDENTIFY_TOLERANCE)) {
    limitNote = `${densityText} is less dense than every substance in the reviewed table (least dense: ${formatDensity(LEAST_DENSE)} g/cm³). The model does not guess a substance; check the mass and volume.`;
  }
  return {
    densityGPerCm3: density,
    match,
    nearest,
    nearestDifference,
    floatResult,
    steps,
    result: `Result: ${densityText} · ${matchText} · ${floatText}`,
    limitNote,
  };
}

// Compression -----------------------------------------------------------------------------------

export interface CompressionExploreSetup {
  startVolumeMl: number;
  pressureMultiplier: number;
}

export const COMPRESSION_FIELDS: ModelField<keyof CompressionExploreSetup>[] = [
  { key: 'startVolumeMl', label: 'Starting volume', unit: 'mL', min: 1, max: 100, step: 1 },
  { key: 'pressureMultiplier', label: 'Pressure multiplier', unit: '×', min: 1, max: 10, step: 0.1 },
];

export const DEFAULT_COMPRESSION_SETUP: CompressionExploreSetup = { startVolumeMl: 50, pressureMultiplier: 2 };

/** The substance each state stands for in the syringe, matching the reviewed compressibility data. */
export const COMPRESSION_SUBSTANCE: Record<MatterState, string> = { gas: 'Air', liquid: 'Water', solid: 'Iron' };

export interface CompressionExplore extends ExploreResult {
  endVolumeMl: number;
  changeMl: number;
}

export function exploreCompression(state: MatterState, setup: CompressionExploreSetup): CompressionExplore {
  const error = firstModelError(COMPRESSION_FIELDS, setup);
  if (error) throw new Error(error);
  const endVolume = compressedVolumeMl(state, setup.startVolumeMl, setup.pressureMultiplier);
  if (endVolume === null) throw new Error('The compression model has no result for this setup.');

  const { startVolumeMl: v1, pressureMultiplier: k } = setup;
  const p2 = START_PRESSURE_KPA * k;
  const change = v1 - endVolume;
  const name = COMPRESSION_SUBSTANCE[state];
  const v1Text = `${formatModelNumber(v1, 1)} mL`;
  const pressureStep: WorkedStep = {
    id: 'pressure',
    title: 'New pressure',
    formula: 'P₂ = multiplier × P₁',
    working: `${formatModelNumber(k, 1)} × ${START_PRESSURE_KPA} kPa = ${formatModelNumber(p2, 0)} kPa`,
    status: 'done',
  };

  let volumeSteps: WorkedStep[];
  if (state === 'gas') {
    volumeSteps = [
      {
        id: 'volume',
        title: 'Boyle’s law (constant temperature)',
        formula: 'V₂ = V₁ × P₁ ÷ P₂',
        working: `${v1Text} × ${START_PRESSURE_KPA} kPa ÷ ${formatModelNumber(p2, 0)} kPa = ${formatModelNumber(endVolume, 2)} mL`,
        status: 'done',
      },
    ];
  } else {
    const beta = COMPRESSIBILITY_PER_PA[state];
    const extraPa = (k - 1) * START_PRESSURE_KPA * PASCALS_PER_KPA;
    volumeSteps = [
      {
        id: 'squeeze',
        title: `${name} barely squashes`,
        formula: 'ΔV = V₁ × β × ΔP',
        working:
          change > 0
            ? `${v1Text} × ${formatScientific(beta)} Pa⁻¹ × ${formatModelNumber(extraPa, 0)} Pa = ${formatScientific(change)} mL`
            : `ΔP = 0 Pa, so ΔV = 0 mL`,
        status: 'done',
      },
      {
        id: 'volume',
        title: 'New volume',
        formula: 'V₂ = V₁ − ΔV',
        working: `${v1Text} − ${change > 0 ? formatScientific(change) : '0'} mL = ${formatModelNumber(endVolume, 6)} mL`,
        status: 'done',
      },
    ];
  }

  const percent = change / v1;
  const steps: WorkedStep[] = [
    pressureStep,
    ...volumeSteps,
    {
      id: 'change',
      title: 'How much smaller',
      formula: '(V₁ − V₂) ÷ V₁ × 100',
      working:
        state === 'gas'
          ? `(${v1Text} − ${formatModelNumber(endVolume, 2)} mL) ÷ ${v1Text} × 100 = ${formatPercent(percent)}`
          : `${change > 0 ? formatScientific(change) : '0'} mL ÷ ${v1Text} × 100 = ${percent > 0 ? formatScientific(percent * 100) : '0'}%`,
      status: 'done',
    },
  ];

  const limitNote =
    k >= COMPRESSION_FIELDS[1].max
      ? `Model limit: ${formatModelNumber(k, 1)} × ${START_PRESSURE_KPA} kPa = ${formatModelNumber(p2, 0)} kPa is the highest pressure this model covers. Boyle’s law treats air as an ideal gas; the model does not go further.`
      : null;
  return {
    endVolumeMl: endVolume,
    changeMl: change,
    steps,
    result:
      state === 'gas'
        ? `Result: ${name} ${v1Text} → ${formatModelNumber(endVolume, 2)} mL (${formatPercent(percent)} smaller)`
        : `Result: ${name} ${v1Text} → ${formatModelNumber(endVolume, 6)} mL (almost no change)`,
    limitNote,
  };
}

// Diffusion -------------------------------------------------------------------------------------

export interface DiffusionExploreSetup {
  temperatureC: number;
}

/**
 * The field accepts a wider range than the model so that a temperature outside the reviewed
 * 0–100 °C data reaches the model and stops there with a limit note instead of a field error.
 */
export const DIFFUSION_FIELDS: ModelField<keyof DiffusionExploreSetup>[] = [
  { key: 'temperatureC', label: 'Temperature', unit: '°C', min: -50, max: 150, step: 1 },
];

export const DEFAULT_DIFFUSION_SETUP: DiffusionExploreSetup = { temperatureC: 80 };

export const DIFFUSION_SUBSTANCE: Record<MatterState, string> = {
  gas: 'Small gas molecule in air',
  liquid: 'Small molecule in water',
  solid: 'Atom in a solid',
};

const DIFFUSION_SHORT_NAME: Record<MatterState, string> = { gas: 'Air', liquid: 'Water', solid: 'Solid' };

export function diffusionSetupLabel(state: MatterState, temperatureC: number): string {
  return `${DIFFUSION_SHORT_NAME[state]} at ${formatModelNumber(temperatureC, 1)} °C`;
}

export interface DiffusionExplore extends ExploreResult {
  /** D at the learner's temperature (m²/s), or null outside the model range. */
  coefficient: number | null;
  /** D(T) ÷ D(20 °C) for the same state, or null outside the model range. */
  ratio: number | null;
  /** The two boxes to draw when the result is valid: the learner's temperature and 20 °C. */
  comparison: { first: DiffusionSetup; second: DiffusionSetup } | null;
}

export function exploreDiffusion(state: MatterState, setup: DiffusionExploreSetup): DiffusionExplore {
  const error = firstModelError(DIFFUSION_FIELDS, setup);
  if (error) throw new Error(error);
  const t = setup.temperatureC;
  const kelvin = t + KELVIN_OFFSET;
  const referenceKelvin = DIFFUSION_REFERENCE_C + KELVIN_OFFSET;
  const base = TYPICAL_DIFFUSION_COEFFICIENT[state];
  const referenceStep: WorkedStep = {
    id: 'reference',
    title: `D at ${DIFFUSION_REFERENCE_C} °C`,
    formula: 'D₂₀ (typical order of magnitude)',
    working: `${formatScientific(base)} m²/s`,
    status: 'done',
  };
  const coefficient = diffusionCoefficient(state, t);
  const tText = `${formatModelNumber(t, 1)} °C`;

  if (coefficient === null) {
    return {
      coefficient: null,
      ratio: null,
      comparison: null,
      steps: [
        referenceStep,
        { id: 'temperature', title: `D at ${tText}`, formula: 'D = D₂₀ × factor', working: '', status: 'not-reached', note: 'outside the model' },
        { id: 'ratio', title: 'Compare with 20 °C', formula: 'D ÷ D₂₀', working: '', status: 'not-reached' },
      ],
      result: `No result at ${tText}.`,
      limitNote: `The reviewed diffusion data covers ${DIFFUSION_MIN_TEMPERATURE_C}–${DIFFUSION_MAX_TEMPERATURE_C} °C. ${tText} is outside it, so the model stops and does not extrapolate.`,
    };
  }

  const kText = `${formatModelNumber(kelvin, 2)} K`;
  const refKText = `${formatModelNumber(referenceKelvin, 2)} K`;
  let formula: string;
  let working: string;
  if (state === 'gas') {
    formula = `D = D₂₀ × (T ÷ T₂₀)^${GAS_DIFFUSION_TEMPERATURE_EXPONENT}`;
    working = `${formatScientific(base)} × (${kText} ÷ ${refKText})^${GAS_DIFFUSION_TEMPERATURE_EXPONENT} = ${formatScientific(coefficient)} m²/s`;
  } else if (state === 'liquid') {
    formula = 'D = D₂₀ × (T ÷ T₂₀) × (η₂₀ ÷ η)';
    working = `${formatScientific(base)} × (${kText} ÷ ${refKText}) × (${formatModelNumber(waterViscosity(DIFFUSION_REFERENCE_C), 3)} ÷ ${formatModelNumber(waterViscosity(t), 3)} mPa·s) = ${formatScientific(coefficient)} m²/s`;
  } else {
    formula = 'D = D₂₀ × (T ÷ T₂₀)';
    working = `${formatScientific(base)} × (${kText} ÷ ${refKText}) = ${formatScientific(coefficient)} m²/s`;
  }
  const ratio = coefficient / base;
  const ratioText = `${formatModelNumber(ratio, 2)}×`;
  const name = DIFFUSION_SHORT_NAME[state];
  return {
    coefficient,
    ratio,
    comparison: {
      first: { state, temperatureC: t, label: diffusionSetupLabel(state, t) },
      second: { state, temperatureC: DIFFUSION_REFERENCE_C, label: diffusionSetupLabel(state, DIFFUSION_REFERENCE_C) },
    },
    steps: [
      referenceStep,
      { id: 'temperature', title: `D at ${tText}`, formula, working, status: 'done' },
      {
        id: 'ratio',
        title: 'Compare with 20 °C',
        formula: 'D ÷ D₂₀',
        working: `${formatScientific(coefficient)} ÷ ${formatScientific(base)} = ${ratioText}`,
        status: 'done',
      },
    ],
    result: `Result: ${name} at ${tText} spreads ${ratioText} as fast as at ${DIFFUSION_REFERENCE_C} °C`,
    limitNote: null,
  };
}

// Teaching goal (CT-INTERACT C7) ----------------------------------------------------------------
// Each check reads only the Explore result and the setup that produced it, never the UI.

export interface DensityChallengeInput {
  setup: DensityExploreSetup;
  result: DensityExplore;
}

export const DENSITY_QUESTION = 'Does a heavier object always sink, or does something else decide it?';

/** The aluminium block's mass, used by the "heavy but floats" task. */
const DENSITY_TASK_HEAVY_G = DEFAULT_DENSITY_SETUP.massG;
/** "At least twice as big" as the default 30 cm³ block. */
const DENSITY_TASK_BIG_CM3 = 2 * DEFAULT_DENSITY_SETUP.volumeCm3;

export const DENSITY_CHALLENGES: ExploreChallenge<DensityChallengeInput>[] = [
  {
    id: 'make-it-float',
    prompt: 'Change the mass or the volume until the sample floats in water.',
    hint: 'Floating needs a density below water’s 0.998 g/cm³.',
    isMet: ({ result }) => result.floatResult === 'floats',
    why: 'A sample floats when its density is less than water’s 0.998 g/cm³. An ice cube is 22.9 g ÷ 25 cm³ = 0.917 g/cm³, so it floats.',
  },
  {
    id: 'bigger-aluminium',
    prompt: `Make a block that still matches Aluminium but is at least twice as big (${DENSITY_TASK_BIG_CM3} cm³ or more).`,
    hint: 'If you double the volume, what must happen to the mass?',
    isMet: ({ setup, result }) => result.match?.id === 'aluminium' && setup.volumeCm3 >= DENSITY_TASK_BIG_CM3,
    why: 'A bigger block is not denser: 162 g ÷ 60 cm³ = 2.70 g/cm³, the same as 81 g ÷ 30 cm³. Density belongs to the material, not to the size of the piece.',
  },
  {
    id: 'heavy-floater',
    prompt: `Make a sample heavier than the aluminium block (more than ${DENSITY_TASK_HEAVY_G} g) that still floats.`,
    hint: 'Give the extra mass plenty of volume.',
    isMet: ({ setup, result }) => setup.massG > DENSITY_TASK_HEAVY_G && result.floatResult === 'floats',
    why: 'Heavy things do not always sink: 500 g ÷ 600 cm³ = 0.833 g/cm³, less than water, so it floats. Mass alone does not decide; mass ÷ volume does.',
  },
];

export interface CompressionChallengeInput {
  state: MatterState;
  setup: CompressionExploreSetup;
  result: CompressionExplore;
}

export const COMPRESSION_QUESTION = 'Why can you squash the air in a sealed syringe but hardly squash water or iron?';

/** Air squashed to a quarter of its start volume, accepted from 24 % to 26 % left. */
const QUARTER_MIN = 0.24;
const QUARTER_MAX = 0.26;
const COMPRESSION_MAX_VOLUME_ML = COMPRESSION_FIELDS[0].max;
const COMPRESSION_MAX_MULTIPLIER = COMPRESSION_FIELDS[1].max;

export const COMPRESSION_CHALLENGES: ExploreChallenge<CompressionChallengeInput>[] = [
  {
    id: 'squeeze-water',
    prompt: 'Put water in the syringe and push harder than the starting pressure. How much smaller does it get?',
    hint: 'Choose Water, then use a pressure multiplier above 1 ×.',
    isMet: ({ state, setup }) => state === 'liquid' && setup.pressureMultiplier > 1,
    why: 'At 2 × pressure, 50 mL of water shrinks by only 0.0023 mL, because its particles already touch. There are almost no gaps to close.',
  },
  {
    id: 'quarter-air',
    prompt: 'Squash the air to a quarter of its starting volume (24 % to 26 % of it left).',
    hint: 'Pressure × volume stays the same for air at constant temperature.',
    isMet: ({ state, setup, result }) => {
      if (state !== 'gas') return false;
      const left = result.endVolumeMl / setup.startVolumeMl;
      return left >= QUARTER_MIN && left <= QUARTER_MAX;
    },
    why: 'Four times the pressure gives a quarter of the volume: 50 mL × 100 kPa = 12.5 mL × 400 kPa. The gas particles do not shrink; the gaps between them close up.',
  },
  {
    id: 'iron-limit',
    prompt: `Squash iron as much as this model allows: ${COMPRESSION_MAX_VOLUME_ML} mL at ${COMPRESSION_MAX_MULTIPLIER} × pressure.`,
    hint: 'Choose Iron, then set both inputs to their highest values.',
    isMet: ({ state, setup }) =>
      state === 'solid' &&
      setup.startVolumeMl >= COMPRESSION_MAX_VOLUME_ML &&
      setup.pressureMultiplier >= COMPRESSION_MAX_MULTIPLIER,
    why: 'Even at 1,000 kPa, 100 mL of iron shrinks by only 0.000529 mL, while the same air would go to 10 mL. Particles in a solid touch and are held in place.',
  },
];

export interface DiffusionChallengeInput {
  state: MatterState;
  setup: DiffusionExploreSetup;
  result: DiffusionExplore;
}

export const DIFFUSION_QUESTION = 'How does temperature change how fast particles spread out?';

/** Slower than at 20 °C, with a margin so that 19 °C (0.97×) does not count. */
const SLOWER_RATIO = 0.95;
/** "Twice as fast", accepted from 1.9× to 2.1×. */
const DOUBLE_MIN = 1.9;
const DOUBLE_MAX = 2.1;
const HOT_AIR_C = 80;

export const DIFFUSION_CHALLENGES: ExploreChallenge<DiffusionChallengeInput>[] = [
  {
    id: 'slower-than-room',
    prompt: 'Make the dye spread more slowly than it does at 20 °C.',
    hint: 'Particles move more slowly when they have less energy.',
    isMet: ({ result }) => result.ratio !== null && result.ratio < SLOWER_RATIO,
    why: 'Colder particles move more slowly, so they spread more slowly: water at 10 °C spreads only 0.74× as fast as at 20 °C.',
  },
  {
    id: 'double-in-water',
    prompt: 'Find the temperature where dye in water spreads twice as fast as at 20 °C (1.9× to 2.1×).',
    hint: 'Doubling 20 °C to 40 °C is not enough. Try higher.',
    isMet: ({ state, result }) =>
      state === 'liquid' && result.ratio !== null && result.ratio >= DOUBLE_MIN && result.ratio <= DOUBLE_MAX,
    why: 'Doubling the Celsius number does not double the speed: 40 °C gives only 1.64×. You need about 50 °C (2.02×), because particle energy follows the kelvin temperature.',
  },
  {
    id: 'hot-air',
    prompt: `Now heat the air to ${HOT_AIR_C} °C or more. Does heating help a gas as much as it helps water?`,
    hint: 'Choose the gas, then compare its result with water at 80 °C (3.4×).',
    isMet: ({ state, setup, result }) => state === 'gas' && setup.temperatureC >= HOT_AIR_C && result.ratio !== null,
    why: 'Heating air from 20 °C to 80 °C speeds diffusion only 1.39×, but water 3.4×, because warm water also becomes runnier. Gas particles still spread about 10,000 times faster than in water.',
  },
];
